// src/pages/SystemHealth.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Database, Webhook, RefreshCw, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { Server, Radio } from 'lucide-react';
import { systemHealthService, PoolStats, PoolHealth, WebhookStats, AsteriskMetrics } from 'api/services/systemHealth.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const bar = (pct: number) => {
  const p = Math.max(0, Math.min(100, pct || 0));
  const color = p >= 90 ? 'bg-red-500' : p >= 75 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${p}%` }} />
    </div>
  );
};

const healthBadge = (status?: string) => {
  const s = (status || 'HEALTHY').toUpperCase();
  if (s === 'HEALTHY') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />Healthy</span>;
  if (s === 'WARNING') return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3" />Warning</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700"><AlertCircle className="w-3 h-3" />{status}</span>;
};

const SystemHealth: React.FC = () => {
  const [pools, setPools] = useState<Record<string, PoolStats>>({});
  const [health, setHealth] = useState<Record<string, PoolHealth>>({});
  const [webhook, setWebhook] = useState<WebhookStats | null>(null);
  const [asterisk, setAsterisk] = useState<AsteriskMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [statsRes, healthRes, wh, ast] = await Promise.all([
      systemHealthService.poolStats(),
      systemHealthService.poolHealth(),
      systemHealthService.webhookStats(),
      systemHealthService.asteriskMetrics(),
    ]);
    if (statsRes.success) setPools(statsRes.data || {}); else setError(statsRes.error || 'Failed to load');
    if (healthRes.success) setHealth(healthRes.data || {});
    setWebhook(wh);
    setAsterisk(ast.success ? (ast.data || null) : null);
    setLoading(false);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const poolNames = Object.keys(pools);
  const totals = poolNames.reduce((a, k) => {
    a.active += pools[k].activeConnections || 0;
    a.total += pools[k].totalConnections || 0;
    a.max += pools[k].maxPoolSize || 0;
    a.waiting += pools[k].threadsAwaiting || 0;
    return a;
  }, { active: 0, total: 0, max: 0, waiting: 0 });

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><Activity className="w-6 h-6 text-blue-600" /> System Health</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">DB connection pools (per master + tenant) and the webhook queue. Auto-refreshes every 15s.</p>
        </div>
        <button onClick={load} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      {loading && poolNames.length === 0 ? <LoadingSpinner /> : (
        <>
          {/* Pool totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500">DB pools</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{poolNames.length}</p></div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500">Active connections</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{totals.active}<span className="text-sm text-gray-400"> / {totals.max}</span></p></div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500">Open connections</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{totals.total}</p></div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"><p className="text-xs text-gray-500">Threads waiting</p><p className={`text-2xl font-bold ${totals.waiting > 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>{totals.waiting}</p></div>
          </div>

          {/* Per-pool table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2"><Database className="w-4 h-4 text-blue-600" /><span className="font-semibold text-gray-800 dark:text-white text-sm">Database connection pools</span></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>{['Pool', 'Active / Max', 'Idle', 'Total', 'Utilization', 'Health'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {poolNames.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">No pools reported.</td></tr>}
                  {poolNames.map(name => {
                    const s = pools[name]; const h = health[name];
                    const util = h?.utilizationPercent ?? (s.maxPoolSize ? (s.activeConnections / s.maxPoolSize) * 100 : 0);
                    return (
                      <tr key={name} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-800 dark:text-gray-200">{name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{s.activeConnections} / {s.maxPoolSize}{s.threadsAwaiting > 0 && <span className="ml-2 text-xs text-red-500">{s.threadsAwaiting} waiting</span>}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{s.idleConnections}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{s.totalConnections}</td>
                        <td className="px-4 py-3 w-48"><div className="flex items-center gap-2">{bar(util)}<span className="text-xs text-gray-500 w-10 text-right">{Math.round(util)}%</span></div></td>
                        <td className="px-4 py-3">{healthBadge(h?.status)}{h?.message && h.status !== 'HEALTHY' && <p className="text-xs text-gray-400 mt-0.5">{h.message}</p>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Webhook queue */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-3"><Webhook className="w-4 h-4 text-purple-600" /><span className="font-semibold text-gray-800 dark:text-white text-sm">Webhook consumer queue</span></div>
            {webhook ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[['Waiting', webhook.waiting, 'text-amber-600'], ['Active', webhook.active, 'text-blue-600'], ['Completed', webhook.completed, 'text-green-600'], ['Failed', webhook.failed, 'text-red-600']].map(([label, v, c]) => (
                  <div key={label as string} className="text-center"><p className="text-xs text-gray-500">{label}</p><p className={`text-2xl font-bold ${v ? (c as string) : 'text-gray-400'}`}>{(v as number) ?? 0}</p></div>
                ))}
              </div>
            ) : <p className="text-sm text-gray-400">Webhook stats unavailable (consumer not running or Redis empty).</p>}
          </div>

          {/* SIP edge — Asterisk res_prometheus */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-3"><Server className="w-4 h-4 text-indigo-600" /><span className="font-semibold text-gray-800 dark:text-white text-sm">SIP edge — Asterisk</span>
              {asterisk && (asterisk.reachable
                ? <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />reachable</span>
                : <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700"><AlertCircle className="w-3 h-3" />unreachable</span>)}
            </div>
            {!asterisk ? (
              <p className="text-sm text-gray-400">No Asterisk metrics endpoint configured (set asterisk.metrics.url, or enable res_prometheus on the box).</p>
            ) : !asterisk.reachable ? (
              <p className="text-sm text-red-500">Cannot reach /metrics{asterisk.error ? ` — ${asterisk.error}` : ''}.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-3">
                  {[['Live channels', asterisk.channels], ['Active calls', asterisk.calls_current], ['Total calls', asterisk.calls_total], ['Bridges', asterisk.bridges], ['Trunks', asterisk.endpoints_count]].map(([l, v]) => (
                    <div key={l as string}><p className="text-xs text-gray-500">{l}</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{(v as number) ?? 0}</p></div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mb-2">Asterisk {asterisk.version} · up {Math.floor((asterisk.uptime_seconds || 0) / 3600)}h</p>
                <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                  <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1"><Radio className="w-3 h-3" /> Trunk SIP state (qualify)</p>
                  <div className="space-y-1">
                    {(asterisk.endpoints || []).map(ep => (
                      <div key={ep.id} className="flex items-center justify-between text-sm">
                        <span className="font-mono text-gray-700 dark:text-gray-300">{ep.id}{ep.channels ? <span className="ml-2 text-xs text-gray-400">{ep.channels} ch</span> : ''}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${ep.state_text === 'online' ? 'bg-green-100 text-green-700' : ep.state_text === 'offline' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{ep.state_text}</span>
                      </div>
                    ))}
                    {(asterisk.endpoints || []).length === 0 && <p className="text-xs text-gray-400">No PJSIP endpoints reported.</p>}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SystemHealth;
